import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import styles from './Chats.module.css';
import { FaUser, FaBookOpen, FaEllipsisV, FaTrash } from 'react-icons/fa';
import type { ChatListResponse, ScenarioListResponse } from '../api/types';

const USE_TEST_DATA = false; // Toggle this for development

interface TestChat {
  id: string;
  display_name: string;
  icon: string;
  time_updated: string;
}

const TEST_CHATS: TestChat[] = [
  {
    id: "test-1",
    display_name: "Mario",
    icon: "/media/characters/mario.png",
    time_updated: new Date().toISOString()
  },
  {
    id: "test-2",
    display_name: "Luigi",
    icon: "/media/characters/luigi.png",
    time_updated: new Date().toISOString()
  },
  {
    id: "test-3",
    display_name: "Mario",
    icon: "/media/characters/mario.png",
    time_updated: new Date().toISOString()
  },
  {
    id: "test-4",
    display_name: "Luigi",
    icon: "/media/characters/luigi.png",
    time_updated: new Date().toISOString()
  }
];

// Type for location state
interface LocationState {
  activeTab?: string;
}

// Type for categorized scenarios
interface CategorizedScenarios {
  today: ScenarioListResponse[];
  yesterday: ScenarioListResponse[];
  lastSevenDays: ScenarioListResponse[];
  older: ScenarioListResponse[];
}

// Type for categorized chats
interface CategorizedChats {
  today: ChatListResponse[];
  yesterday: ChatListResponse[];
  lastSevenDays: ChatListResponse[];
  older: ChatListResponse[];
}

function Chats(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState | null;
  const { user } = useAuth();
  const [recentChats, setRecentChats] = useState<ChatListResponse[]>([]);
  const [recentScenarios, setRecentScenarios] = useState<ScenarioListResponse[]>([]); // User's recent scenarios
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [recentScenariosLoading, setRecentScenariosLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [recentScenariosError, setRecentScenariosError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>(
    locationState?.activeTab || 'characters'
  );
  const [menuOpen, setMenuOpen] = useState<string | null>(null); // Track which menu is open
  
  // Clear the location state after setting the active tab
  useEffect(() => {
    if (locationState?.activeTab) {
      // Replace the current entry in the history stack with one without the state
      window.history.replaceState({}, document.title);
    }
  }, [locationState]);
  
  const fetchChats = async (): Promise<void> => {
    try {
      if (USE_TEST_DATA) {
        setRecentChats(TEST_CHATS);
        setIsLoading(false);
        return;
      }

      const chats = await api.chats.list();
      
      // Sort chats by time_updated in descending order (newest first)
      chats.sort((a, b) => new Date(b.time_updated).getTime() - new Date(a.time_updated).getTime());
      
      setRecentChats(chats);
      setIsLoading(false);
    } catch (err) {
      console.error('Failed to fetch chats:', err);
      setError('Failed to load chats');
      setIsLoading(false);
    }
  };

  const fetchRecentScenarios = async (): Promise<void> => {
    try {
      setRecentScenariosLoading(true);
      
      // Only fetch if user is logged in
      if (user) {
        const recentScenariosList = await api.scenarios.list();
        
        setRecentScenarios(recentScenariosList);
      }
      setRecentScenariosLoading(false);
    } catch (err) {
      console.error('Failed to fetch recent scenarios:', err);
      setRecentScenariosError('Failed to load recent scenarios');
      setRecentScenariosLoading(false);
    }
  };

  useEffect(() => {
    fetchChats();
  }, []);

  useEffect(() => {
    fetchRecentScenarios();
  }, [user]);

  // Helper function to check if a date is today
  const isToday = (date: string): boolean => {
    const today = new Date();
    const scenarioDate = new Date(date);
    return (
      scenarioDate.getDate() === today.getDate() &&
      scenarioDate.getMonth() === today.getMonth() &&
      scenarioDate.getFullYear() === today.getFullYear()
    );
  };

  // Helper function to check if a date is yesterday
  const isYesterday = (date: string): boolean => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const scenarioDate = new Date(date);
    return (
      scenarioDate.getDate() === yesterday.getDate() &&
      scenarioDate.getMonth() === yesterday.getMonth() &&
      scenarioDate.getFullYear() === yesterday.getFullYear()
    );
  };

  // Helper function to check if a date is within last 7 days
  const isLastSevenDays = (date: string): boolean => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const scenarioDate = new Date(date);
    return scenarioDate >= sevenDaysAgo && !isToday(date) && !isYesterday(date);
  };

  // Helper function to categorize scenarios
  const categorizeScenarios = (scenarios: ScenarioListResponse[]): CategorizedScenarios => {
    const categorized: CategorizedScenarios = {
      today: [],
      yesterday: [],
      lastSevenDays: [],
      older: []
    };

    scenarios.sort((a, b) => new Date(b.time_updated).getTime() - new Date(a.time_updated).getTime());

    scenarios.forEach(scenario => {
      const date = scenario.time_updated;
      if (isToday(date)) {
        categorized.today.push(scenario);
      } else if (isYesterday(date)) {
        categorized.yesterday.push(scenario);
      } else if (isLastSevenDays(date)) {
        categorized.lastSevenDays.push(scenario);
      } else {
        categorized.older.push(scenario);
      }
    });

    return categorized;
  };

  // Helper function to categorize chats
  const categorizeChats = (chats: ChatListResponse[]): CategorizedChats => {
    const categorized: CategorizedChats = {
      today: [],
      yesterday: [],
      lastSevenDays: [],
      older: []
    };

    chats.forEach(chat => {
      const date = chat.time_updated;
      if (isToday(date)) {
        categorized.today.push(chat);
      } else if (isYesterday(date)) {
        categorized.yesterday.push(chat);
      } else if (isLastSevenDays(date)) {
        categorized.lastSevenDays.push(chat);
      } else {
        categorized.older.push(chat);
      }
    });

    return categorized;
  };

  const handleTabChange = (tab: string): void => {
    setActiveTab(tab);
  };

  // Handle menu toggle
  const toggleMenu = (e: React.MouseEvent, id: string): void => {
    e.stopPropagation(); // Prevent card click
    setMenuOpen(menuOpen === id ? null : id);
  };

  // Handle removing a chat from recents
  const handleRemoveChat = async (e: React.MouseEvent, chatId: string): Promise<void> => {
    e.stopPropagation(); // Prevent card click
    
    try {
      // Get the chat instance
      const chat = await api.chats.get(chatId);
      
      // Perform the delete operation
      await chat.delete();
      
      // Only update the UI after successful deletion
      setRecentChats(prevChats => prevChats.filter(chat => chat.id !== chatId));
      setMenuOpen(null); // Close the menu
      
    } catch (err) {
      console.error('Failed to remove chat:', err);
      alert('Failed to remove chat from recents. Please try again.');
    }
  };

  // Handle removing a scenario from recents
  const handleRemoveScenario = async (e: React.MouseEvent, scenarioId: string): Promise<void> => {
    e.stopPropagation(); // Prevent card click
    
    try {
      // Get the scenario instance
      const scenario = await api.scenarios.get(scenarioId);
      
      // Perform the delete operation
      await scenario.delete();
      
      // Only update the UI after successful deletion
      setRecentScenarios(prevScenarios => prevScenarios.filter(scenario => scenario.id !== scenarioId));
      setMenuOpen(null); // Close the menu
      
    } catch (err) {
      console.error('Failed to remove scenario:', err);
      alert('Failed to remove scenario from recents. Please try again.');
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (): void => {
      setMenuOpen(null);
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Helper function to render a chat section
  const renderChatSection = (chats: ChatListResponse[] | null, title: string): JSX.Element | null => {
    if (!chats || chats.length === 0) return null;

    return (
      <div key={title} className={styles.scenarioSection}>
        <h3 className={styles.sectionHeader}>{title}</h3>
        <div className={styles.itemsContainer}>
          {chats.map((chat) => (
            <div 
              key={chat.id} 
              className={styles.chatItem}
              onClick={() => navigate(`/chats/${chat.id}`, { state: { sourceUrl: '/chats' } })}
            >
              <div className={styles.avatarWrapper}>
                <img src={chat.icon} alt={chat.display_name} className={styles.chatAvatar} />
              </div>
              <span className={styles.chatName}>{chat.display_name}</span>
              <div className={styles.menuContainer} onClick={(e) => e.stopPropagation()}>
                <button 
                  className={styles.menuButton} 
                  onClick={(e) => toggleMenu(e, `chat-${chat.id}`)}
                >
                  <FaEllipsisV />
                </button>
                {menuOpen === `chat-${chat.id}` && (
                  <div className={styles.menuDropdown}>
                    <button 
                      className={styles.menuItem}
                      onClick={(e) => handleRemoveChat(e, chat.id)}
                    >
                      <FaTrash className={styles.menuIcon} />
                      <span>Remove from recents</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCharactersTab = (): JSX.Element => {
    if (isLoading) {
      return <div className={styles.loadingContainer}>Loading...</div>;
    }
    
    if (error) {
      return <div className={styles.errorContainer}>{error}</div>;
    }
    
    if (recentChats.length === 0) {
      return <div className={styles.emptyState}>No recent chats</div>;
    }
    
    const categorizedChats = categorizeChats(recentChats);
    
    return (
      <div className={styles.scenariosContainer}>
        {renderChatSection(categorizedChats.today, 'Today')}
        {renderChatSection(categorizedChats.yesterday, 'Yesterday')}
        {renderChatSection(categorizedChats.lastSevenDays, 'Last 7 Days')}
        {renderChatSection(categorizedChats.older, 'Older')}
      </div>
    );
  };

  const renderScenarioSection = (scenarios: ScenarioListResponse[] | null, title: string): JSX.Element | null => {
    if (!scenarios || scenarios.length === 0) return null;

    return (
      <div key={title} className={styles.scenarioSection}>
        <h3 className={styles.sectionHeader}>{title}</h3>
        <div className={styles.itemsContainer}>
          {scenarios.map((scenario) => (
            <div 
              key={scenario.id} 
              className={styles.scenarioItem}
              onClick={() => {
                // Use the API to get the scenario instance and then call bump
                api.scenarios.get(scenario.id)
                  .then(scenarioInstance => {
                    return scenarioInstance.bump();
                  })
                  .then(() => {
                    navigate(`/sessions/${scenario.id}`);
                  })
                  .catch(error => {
                    console.error('Failed to bump scenario:', error);
                    // Still navigate even if bump fails
                    navigate(`/sessions/${scenario.id}`);
                  });
              }}
            >
              <div className={styles.avatarWrapper}>
                <img 
                  src={scenario.definition.icon} 
                  alt={scenario.definition.display_name} 
                  className={styles.scenarioAvatar} 
                />
              </div>
              <div className={styles.scenarioInfo}>
                <span className={styles.scenarioName}>
                  {scenario.definition.display_name}
                </span>
                <span className={styles.scenarioTagline}>
                  {scenario.definition.tagline}
                </span>
              </div>
              <div className={styles.menuContainer} onClick={(e) => e.stopPropagation()}>
                <button 
                  className={styles.menuButton} 
                  onClick={(e) => toggleMenu(e, `scenario-${scenario.id}`)}
                >
                  <FaEllipsisV />
                </button>
                {menuOpen === `scenario-${scenario.id}` && (
                  <div className={styles.menuDropdown}>
                    <button 
                      className={styles.menuItem}
                      onClick={(e) => handleRemoveScenario(e, scenario.id)}
                    >
                      <FaTrash className={styles.menuIcon} />
                      <span>Remove from recents</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderScenariosTab = (): JSX.Element => {
    // Show loading state if recent scenarios are loading
    if (recentScenariosLoading) {
      return <div className={styles.loadingContainer}>Loading...</div>;
    }
    
    // Show error if there's an error with recent scenarios
    if (recentScenariosError) {
      return <div className={styles.errorContainer}>
        {recentScenariosError}
      </div>;
    }
    
    // Show empty state if there are no recent scenarios
    if (!user || recentScenarios.length === 0) {
      return <div className={styles.emptyState}>No recent scenarios</div>;
    }

    const categorizedScenarios = categorizeScenarios(recentScenarios);
    
    return (
      <div className={styles.scenariosContainer}>
        {renderScenarioSection(categorizedScenarios.today, 'Today')}
        {renderScenarioSection(categorizedScenarios.yesterday, 'Yesterday')}
        {renderScenarioSection(categorizedScenarios.lastSevenDays, 'Last 7 Days')}
        {renderScenarioSection(categorizedScenarios.older, 'Older')}
      </div>
    );
  };

  return (
    <div className={styles.chatList}>
      <div className={styles.titleContainer}>
        <div className={styles.titleLine}></div>
        <h2 className={styles.sectionTitle}>Recents</h2>
        <div className={styles.titleLine}></div>
      </div>
      
      <div className={styles.tabsContainer}>
        <button 
          className={`${styles.tabButton} ${activeTab === 'characters' ? styles.activeTab : ''}`}
          onClick={() => handleTabChange('characters')}
        >
          <FaUser className={styles.tabIcon} />
          <span>Characters</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'scenarios' ? styles.activeTab : ''}`}
          onClick={() => handleTabChange('scenarios')}
        >
          <FaBookOpen className={styles.tabIcon} />
          <span>Scenarios</span>
        </button>
      </div>
      
      <div className={styles.tabContent}>
        {activeTab === 'characters' ? renderCharactersTab() : renderScenariosTab()}
      </div>
    </div>
  );
}

export default Chats; 